import axios from 'axios'

// -------------------------------------------------------------------
// MAIN API – For your backend (credentials ON, tenant header ON)
// -------------------------------------------------------------------
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true,
})


// -------------------------------------------------------------------
// EXTERNAL API – For public endpoints (credentials OFF, NO tenant)
// Prevents CORS block on external APIs (e.g. Nager Public Holidays)
// -------------------------------------------------------------------
const externalApi = axios.create({
  withCredentials: false,
})


// -------------------------------------------------------------------
// Helper: determine if URL is external
// -------------------------------------------------------------------
const isExternal = (url) => {
  return url.startsWith('http://') || url.startsWith('https://')
}


// -------------------------------------------------------------------
// Cookie reader (CSRF support)
// -------------------------------------------------------------------
const getCookie = (name) => {
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim()
      if (cookie.substring(0, name.length + 1) === name + '=') {
        return decodeURIComponent(cookie.substring(name.length + 1))
      }
    }
  }
  return null
}


// -------------------------------------------------------------------
// REQUEST INTERCEPTOR – Authorization, Tenant, CSRF
// -------------------------------------------------------------------
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  const hostname = window.location.hostname
  const subdomain = hostname.split('.')[0]

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }

  if (subdomain && subdomain !== 'localhost') {
    config.headers['X-Tenant-Subdomain'] = subdomain
  }

  const csrfToken = getCookie('XSRF-TOKEN')
  if (
    csrfToken &&
    !['GET', 'HEAD', 'OPTIONS'].includes(config.method.toUpperCase())
  ) {
    config.headers['X-XSRF-TOKEN'] = csrfToken
  }

  return config
})


// -------------------------------------------------------------------
// RESPONSE INTERCEPTOR – Handles 401 Unauthorized
// -------------------------------------------------------------------
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token')

      const currentPath = window.location.pathname + window.location.search
      const event = new CustomEvent('unauthorized-redirect', {
        detail: { nextPath: currentPath },
      })
      window.dispatchEvent(event)
    }
    return Promise.reject(error)
  },
)


// -------------------------------------------------------------------
// MAIN EXPORT FUNCTIONS – SAME NAMES (NO BREAKING CHANGES)
// Auto-switches to externalApi if URL is external.
// -------------------------------------------------------------------
export const GetData = async (endPoint, options = {}) => {
  try {
    const client = isExternal(endPoint) ? externalApi : api
    const response = await client.get(endPoint, options)
    return response
  } catch (err) {
    throw err
  }
}

export const PostData = async (endPoint, options = {}) => {
  try {
    const client = isExternal(endPoint) ? externalApi : api
    const response = await client.post(endPoint, options)
    return response
  } catch (err) {
    throw err
  }
}

export const PutData = async (endPoint, options = {}) => {
  try {
    const client = isExternal(endPoint) ? externalApi : api
    const response = await client.put(endPoint, options)
    return response
  } catch (err) {
    throw err
  }
}

export const PatchData = async (endPoint, options = {}) => {
  try {
    const client = isExternal(endPoint) ? externalApi : api
    const response = await client.patch(endPoint, options)
    return response
  } catch (err) {
    throw err
  }
}

export const DeleteData = async (endPoint, options = {}) => {
  try {
    const client = isExternal(endPoint) ? externalApi : api
    const response = await client.delete(endPoint, options)
    return response
  } catch (err) {
    throw err
  }
}


// -------------------------------------------------------------------
// DOWNLOAD DATA (unchanged except using correct client)
// -------------------------------------------------------------------
export const DownloadData = async (
  endPoint,
  filename = 'download',
  fallbackMimeType = 'application/octet-stream',
) => {
  try {
    const client = isExternal(endPoint) ? externalApi : api
    const response = await client.get(endPoint, { responseType: 'blob' })

    const mimeType = response.headers['content-type'] || fallbackMimeType
    const extension = getFileExtensionFromMimeType(mimeType)

    const blob = new Blob([response.data], { type: mimeType })
    const url = window.URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = filename.endsWith(`.${extension}`) ? filename : `${filename}.${extension}`
    document.body.appendChild(a)
    a.click()
    a.remove()

    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Download failed:', error)
    alert('Failed to download file.')
  }
}


// -------------------------------------------------------------------
// MIME → extension map (unchanged)
// -------------------------------------------------------------------
const getFileExtensionFromMimeType = (mimeType) => {
  const map = {
    'application/pdf': 'pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel': 'xls',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'text/csv': 'csv',
    'application/zip': 'zip',
    'application/json': 'json',
  }

  return map[mimeType] || 'bin'
}


export default api
